import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  ListItem,
  TablePagination,
  Typography,
} from "@mui/material";
import React, { ReactNode, useEffect, useState } from "react";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import { Checkbox } from "~frontend/components/Checkbox";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  ThreadListItems,
  ThreadListWrapper,
} from "~frontend/components/threads";

import { useThreadsContext } from "../../../../components/threads/ThreadsProvider";
import BulkActionDialog from "../BulkActionDialog";
import { ThreadListFilters } from "./ThreadListFilters";
import ThreadListThreadItems from "./ThreadListThreadItems";
import { BulkSelectionState } from "./types";

export default function ThreadList(): ReactElement {
  const {
    listThreadsPagination,
    setListThreadsPagination,
    threadId,
    threadsList,
    threadsCount,
  } = useThreadsContext()!;

  const [bulkSelectionState, setBulkSelectionState] =
    useState<BulkSelectionState>({
      state: "inactive",
    });

  const threadListRef = React.useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (threadId && threadListRef.current) {
      threadListRef.current
        ?.querySelector(`[data-thread-id="${threadId}"]`)
        ?.scrollIntoView({ block: "nearest" });
    }
  }, [threadId]);

  let totalCountDisplay: ReactNode = (
    <FormattedMessage
      defaultMessage="many"
      description="Number of threads displayed when there is an error loading the exact number of threads"
    />
  );
  let totalCountForMuiTable = -1;
  if (threadsCount.loading) {
    totalCountDisplay = <CircularProgress size={10} />;
  } else if (threadsCount.data) {
    if (threadsCount.data.totalCountOverLimit) {
      totalCountDisplay = `${threadsCount.data.totalCount.toLocaleString()}+`;
    } else {
      totalCountDisplay = threadsCount.data.totalCount.toLocaleString();
      totalCountForMuiTable = threadsCount.data.totalCount;
    }
  }

  const threads = threadsList.data;

  useEffect(() => {
    if (
      bulkSelectionState.state === "selecting" &&
      bulkSelectionState.selectedThreadIds.length > 0 &&
      threadsList.data
    ) {
      // remove any threads that are no longer in the result set
      const validThreadIds = new Set(threadsList.data.map(r => r.id));
      const newSelectedThreadIds = bulkSelectionState.selectedThreadIds.filter(
        id => validThreadIds.has(id)
      );
      if (
        newSelectedThreadIds.length !==
        bulkSelectionState.selectedThreadIds.length
      ) {
        setBulkSelectionState({
          ...bulkSelectionState,
          selectedThreadIds: newSelectedThreadIds,
        });
      }
    }
  }, [threadsList.data, bulkSelectionState]);

  return (
    <ThreadListWrapper>
      <ThreadListItems ref={threadListRef}>
        <ListItem divider>
          <Box
            sx={{ width: "100%", paddingLeft: "12px", paddingBottom: "12px" }}
          >
            <ThreadListFilters />
            <FlexBox justifyContent="space-between" alignItems="center">
              {bulkSelectionState.state === "inactive" ? (
                <>
                  <Typography variant="body2" sx={{ pl: 0 }}>
                    <FormattedMessage
                      defaultMessage="{threadCount} threads"
                      description="Number of threads in search results"
                      values={{
                        threadCount: totalCountDisplay,
                      }}
                    />
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      height: "28px",
                      fontSize: "10px",
                      fontWeight: 500,
                      padding: "6px 8px",
                    }}
                    color="tertiary"
                    onClick={() =>
                      setBulkSelectionState({
                        selectedThreadIds: [],
                        state: "selecting",
                      })
                    }
                  >
                    <FormattedMessage
                      defaultMessage="Bulk Action"
                      description="Button label to begin selecting threads for bulk action"
                    />
                  </Button>
                </>
              ) : (
                <>
                  <FormControlLabel
                    sx={{
                      fontSize: "10px",
                      marginLeft: "-12px",
                    }}
                    control={
                      <Checkbox
                        sx={{ padding: "0 9px" }}
                        checked={
                          threads
                            ? bulkSelectionState.selectedThreadIds.length ===
                              threads.length
                            : false
                        }
                        onChange={evt => {
                          if (evt.target.checked) {
                            setBulkSelectionState({
                              ...bulkSelectionState,
                              selectedThreadIds: (threads ?? []).map(r => r.id),
                            });
                          } else {
                            setBulkSelectionState({
                              ...bulkSelectionState,
                              selectedThreadIds: [],
                            });
                          }
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "10px" }}>
                        <FormattedMessage
                          defaultMessage="Select All"
                          description="Label for checkbox to select/unselect all threads"
                        />
                      </Typography>
                    }
                  />
                  {bulkSelectionState.selectedThreadIds.length > 0 ? (
                    <Button
                      onClick={() => {
                        setBulkSelectionState({
                          ...bulkSelectionState,
                          state: "modalOpen",
                        });
                      }}
                      sx={{
                        height: "28px",
                        fontSize: "10px",
                        fontWeight: 500,
                        padding: "6px 8px",
                      }}
                      variant="contained"
                      color="primary"
                    >
                      <FormattedMessage
                        defaultMessage={`Bulk act on {
nThreads, plural, =1 {# thread} other {# threads}
}`}
                        description="Label for button to perform bulk action"
                        values={{
                          nThreads: bulkSelectionState.selectedThreadIds.length,
                        }}
                      />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setBulkSelectionState({
                          state: "inactive",
                        });
                      }}
                      sx={{ height: "28px", fontSize: "10px" }}
                      variant="contained"
                      color="tertiary"
                    >
                      <FormattedMessage
                        defaultMessage="Cancel"
                        description="Label for button to cancel bulk action"
                      />
                    </Button>
                  )}
                </>
              )}
            </FlexBox>
          </Box>
        </ListItem>
        <ThreadListThreadItems
          bulkSelectionState={bulkSelectionState}
          setBulkSelectionState={setBulkSelectionState}
        />
      </ThreadListItems>
      <TablePagination
        labelDisplayedRows={({ from, to }) => (
          <FormattedMessage
            defaultMessage="{from}-{to} of {total}"
            description="Pagination label for the range of rows displayed on the current page"
            values={{
              from: from.toLocaleString(),
              to: to.toLocaleString(),
              total: totalCountDisplay,
            }}
          />
        )}
        labelRowsPerPage={
          <FormattedMessage
            defaultMessage="Per page:"
            description="Rows per page label"
          />
        }
        component="div"
        rowsPerPageOptions={[10, 25, 100]}
        // -1 is an indicator to MUI that the total count is unknown
        count={totalCountForMuiTable}
        rowsPerPage={listThreadsPagination.pageSize}
        page={listThreadsPagination.page}
        onPageChange={(_evt, newPage) => {
          if (bulkSelectionState.state === "selecting") {
            setBulkSelectionState({
              ...bulkSelectionState,
              selectedThreadIds: [],
            });
          }

          setListThreadsPagination({
            page: newPage,
            pageSize: listThreadsPagination.pageSize,
          });
        }}
        onRowsPerPageChange={evt => {
          setListThreadsPagination({
            page: 0,
            pageSize: parseInt(evt.target.value, 10),
          });
        }}
      />
      <BulkActionDialog
        open={bulkSelectionState.state === "modalOpen"}
        selectedThreads={
          bulkSelectionState.state === "inactive" || !threads
            ? []
            : threads.filter(thread =>
                bulkSelectionState.selectedThreadIds.includes(thread.id)
              )
        }
        onCloseModal={() => {
          if (bulkSelectionState.state === "inactive") {
            return;
          }
          setBulkSelectionState({
            ...bulkSelectionState,
            state: "selecting",
          });
        }}
        onCompleteAction={() =>
          setBulkSelectionState({
            state: "inactive",
          })
        }
      />
    </ThreadListWrapper>
  );
}
